import { create } from 'zustand';
import { THEMES } from './themes';

/**
 * The "theme lineup" — the ordered set of themes shown in the avatar-menu quick
 * switcher (8 slots, 4 across). The user curates it in the Locker; the dropdown
 * reads it here, so editing one pin reflects everywhere immediately.
 *
 * Client state, so it lives in Zustand (not TanStack Query) and persists to
 * localStorage — same boundary as the active theme / custom upload. (There's no
 * `profiles` column for a lineup array, so this is per-device for now.)
 */
const KEY = 'winr-theme-lineup';
export const THEME_LINEUP_MAX = 8;

function defaultLineup(): string[] {
  return THEMES.slice(0, THEME_LINEUP_MAX).map((t) => t.id);
}

function load(): string[] {
  if (typeof window === 'undefined') return defaultLineup();
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const known = new Set(THEMES.map((t) => t.id));
      const ids = (JSON.parse(raw) as string[]).filter((id) => known.has(id)).slice(0, THEME_LINEUP_MAX);
      if (ids.length) return ids;
    }
  } catch {
    /* corrupt value — fall back to the default lineup */
  }
  return defaultLineup();
}

interface ThemeLineupState {
  /** Ordered theme ids shown in the avatar quick-switcher (≤ THEME_LINEUP_MAX). */
  lineup: string[];
  /** Pin/unpin a theme. Adding past the cap is a no-op (the UI disables it). */
  toggle: (id: string) => void;
}

export const useThemeLineup = create<ThemeLineupState>((set) => ({
  lineup: load(),
  toggle: (id) =>
    set((s) => {
      const next = s.lineup.includes(id)
        ? s.lineup.filter((x) => x !== id)
        : s.lineup.length >= THEME_LINEUP_MAX
          ? s.lineup
          : [...s.lineup, id];
      if (next !== s.lineup) localStorage.setItem(KEY, JSON.stringify(next));
      return { lineup: next };
    }),
}));
